/**
 * LiveEvent list — dashboard showing all trivia events.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Badge,
  Callout,
  Card,
  Code,
  Heading,
  Select,
  Table,
  Text,
  Dialog,
  TextField,
  TextArea,
  Flex,
} from '@radix-ui/themes';
import api from '../../utils/api';
import { usePagination } from '../../utils/usePagination';
import Pagination from '../shared/Pagination';
import HelpTip from '../shared/HelpTip';
import PlanLimitCallout from '../Billing/PlanLimitCallout';
import { planLimitFromError, type PlanLimitDetails } from '../../utils/billing';

interface LiveEvent {
  id: string;
  date: string;
  pin: string;
  status: 'LIVE' | 'COMPLETE';
  // Historical import (round totals only) — not a hostable game.
  roundLevelOnly?: boolean;
  // Display-name override. When null/empty, fall back to quiz.name.
  name: string | null;
  // Per-event welcome overrides. Null → fall back to venue default → quiz.
  prizesOverride: string | null;
  rulesTextOverride: string | null;
  startTimeOverride: string | null;
  venue: { name: string };
  quiz: { name: string };
  _count: { teams: number };
}

interface Venue {
  id: number;
  name: string;
  defaultStartTime?: string | null;
}

// Venue start times are stored as free text for the slides ("7:00 PM", "19:00",
// "7pm"). Parse the common shapes into a 24h "HH:MM" so a <input type="time">
// can be pre-filled from the venue's usual start. Returns '' when unparseable.
function parseTimeToHHMM(s?: string | null): string {
  if (!s) return '';
  const m = s.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i);
  if (!m) return '';
  let h = parseInt(m[1]!, 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = m[3]?.toLowerCase().replace(/\./g, '');
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  if (h > 23 || min > 59) return '';
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

const EventList: React.FC = () => {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', venueId: '', date: '', time: '' });
  const [creating, setCreating] = useState(false);
  const [createLimit, setCreateLimit] = useState<PlanLimitDetails | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [evRes, vRes] = await Promise.all([
        api.get('/api/events'),
        api.get('/venues'),
      ]);
      setEvents(evRes.data.data?.events ?? []);
      setVenues(vRes.data ?? []);
    } catch (err) {
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.venueId || !form.date) return;
    setCreating(true);
    setCreateLimit(null);
    try {
      // Combine the date with the time (venue default already seeded it; fall
      // back to 7pm). Concatenated as a local datetime so it stores the wall
      // time the host picked.
      const time = form.time || '19:00';
      // A night brings its own questions — the server mints them. There is no
      // quiz to choose because a quiz has never been reused across dates.
      const res = await api.post('/api/events', {
        name: form.name.trim(),
        venueId: parseInt(form.venueId),
        date: new Date(`${form.date}T${time}`).toISOString(),
      });
      setShowCreate(false);
      setForm({ name: '', venueId: '', date: '', time: '' });
      navigate(`/events/${res.data.data.event.id}/hub`);
    } catch (err) {
      console.error('Failed to create event', err);
      setCreateLimit(planLimitFromError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (ev: LiveEvent) => {
    const label = ev.name?.trim() || ev.quiz?.name || 'this live event';
    if (!window.confirm(
      `Delete "${label}"?\n\nThis removes the live event and its recorded teams and scores. The quiz itself is kept.`
    )) return;
    const prev = events;
    setEvents((es) => es.filter((e) => e.id !== ev.id)); // optimistic
    try {
      await api.delete(`/api/events/${ev.id}`);
    } catch (err) {
      console.error('Failed to delete event', err);
      setEvents(prev); // roll back
    }
  };

  const statusColor = (status: string): 'green' | 'gray' | 'amber' => {
    switch (status) {
      case 'LIVE': return 'green';
      case 'COMPLETE': return 'gray';
      default: return 'amber';
    }
  };

  const evp = usePagination(events, 12);

  // Per-event editor (display name + welcome overrides).
  const [editTarget, setEditTarget] = useState<LiveEvent | null>(null);
  const [editForm, setEditForm] = useState({ name: '', startTimeOverride: '', prizesOverride: '', rulesTextOverride: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (ev: LiveEvent) => {
    setEditTarget(ev);
    setEditForm({
      name: ev.name ?? '',
      startTimeOverride: ev.startTimeOverride ?? '',
      prizesOverride: ev.prizesOverride ?? '',
      rulesTextOverride: ev.rulesTextOverride ?? '',
    });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      await api.put(`/api/events/${editTarget.id}`, editForm);
      const clean = (s: string) => s.trim() || null;
      setEvents((prev) => prev.map((e) => e.id === editTarget.id ? {
        ...e,
        name: clean(editForm.name),
        startTimeOverride: clean(editForm.startTimeOverride),
        prizesOverride: clean(editForm.prizesOverride),
        rulesTextOverride: clean(editForm.rulesTextOverride),
      } : e));
      setEditTarget(null);
    } catch (err) {
      console.error('Failed to save event', err);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Box p="6">
      <Flex justify="between" align="start" gap="3" wrap="wrap" mb="4">
        <Box>
          <Heading as="h1" size="6">
            Game Nights
            <HelpTip side="bottom">
              A game night is one night at one venue: its questions, host panel, scoring
              screen and projector view. For the recurring schedule shown on your website,
              use Website → Public Schedule instead.
            </HelpTip>
          </Heading>
          <Text size="2" color="gray">One night at one venue. Write it, host it, score it, present it — all from here.</Text>
        </Box>
        <Flex align="center" gap="3">
          <Button onClick={() => setShowCreate(true)}>
            + New Game Night
          </Button>
        </Flex>
      </Flex>

      {showCreate && !loading && venues.length === 0 && (
        <Callout.Root color="amber" mb="3">
          <Callout.Text>
            A game night needs a venue first.{' '}
            <Link to="/venues">Add a venue</Link>, then come back here.
          </Callout.Text>
        </Callout.Root>
      )}

      {showCreate && (
        <Card mb="4">
          <form onSubmit={handleCreate}>
            <Flex align="center" gap="3" wrap="wrap">
              <TextField.Root
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Name this night…"
                aria-label="Night name"
                style={{ minWidth: '15rem' }}
                required
              />

              <Select.Root
                value={form.venueId}
                onValueChange={(val) => setForm(f => {
                  // Seed the time from the venue's usual start (unless the host
                  // already picked one). Keeps scheduling to two clicks.
                  const venue = venues.find(v => String(v.id) === val);
                  const seeded = parseTimeToHHMM(venue?.defaultStartTime);
                  return { ...f, venueId: val, time: f.time || seeded };
                })}
                required
              >
                <Select.Trigger placeholder="Select Venue..." aria-label="Select Venue" />
                <Select.Content>
                  {venues.map(v => (
                    <Select.Item key={v.id} value={String(v.id)}>
                      {v.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>

              <TextField.Root
                type="date"
                value={form.date}
                onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                aria-label="Date"
                required
              />
              <TextField.Root
                type="time"
                value={form.time}
                onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))}
                aria-label="Start time"
                title="Start time — defaults to the venue's usual time"
              />
              <Button type="submit" disabled={creating || venues.length === 0}>
                {creating ? 'Creating...' : 'Create Game Night'}
              </Button>
              <Button variant="soft" color="gray" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
            </Flex>
            {createLimit && <Box mt="3"><PlanLimitCallout details={createLimit} /></Box>}
          </form>
        </Card>
      )}

      {loading ? (
        <Card>
          <Text size="2" color="gray">Loading events...</Text>
        </Card>
      ) : events.length === 0 ? (
        <Card>
          <Flex direction="column" align="center" gap="2" py="6">
            <Heading as="h3" size="3">No game nights yet</Heading>
            <Text size="2" as="p" color="gray" align="center" style={{ maxWidth: 520 }}>
              A game night is one specific run of a quiz at a specific venue on a specific date.
              Creating one generates a 6-digit audience PIN and a 4-digit admin PIN, and gives you
              a host panel, a scoring screen, and a projector view.
            </Text>
            <Button onClick={() => setShowCreate(true)}>+ Create your first game night</Button>
          </Flex>
        </Card>
      ) : (
        <>
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Quiz</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Venue</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                PIN
                <HelpTip side="bottom">
                  The 6-digit code the audience screen uses — open /present/&lt;PIN&gt; on the
                  venue TV or projector to show this game night.
                </HelpTip>
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Teams</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {evp.slice.map((ev) => (
              <Table.Row key={ev.id}>
                <Table.Cell>{new Date(ev.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Table.Cell>
                <Table.Cell>
                  <EventNameCell
                    event={ev}
                    onRenamed={(next) =>
                      setEvents((prev) =>
                        prev.map((e) => (e.id === ev.id ? { ...e, name: next } : e)),
                      )
                    }
                  />
                </Table.Cell>
                <Table.Cell>{ev.venue?.name ?? '—'}</Table.Cell>
                <Table.Cell><Code>{ev.pin}</Code></Table.Cell>
                <Table.Cell>{ev._count?.teams ?? 0}</Table.Cell>
                <Table.Cell>
                  <Badge
                    color={ev.roundLevelOnly ? 'amber' : statusColor(ev.status)}
                    variant="soft"
                  >
                    {ev.roundLevelOnly ? 'IMPORTED' : ev.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap="3" align="center" wrap="wrap">
                    {ev.roundLevelOnly ? (
                      <Button asChild variant="ghost" size="1">
                        <Link to={`/events/${ev.id}/results`}>Results</Link>
                      </Button>
                    ) : (
                      <Button asChild variant="soft" size="1">
                        <Link to={`/events/${ev.id}/hub`}>Open</Link>
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="1"
                      onClick={() => openEdit(ev)}
                      title="Edit name & welcome overrides"
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="1"
                      color="red"
                      onClick={() => handleDelete(ev)}
                      title="Delete this live event"
                    >
                      Delete
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        <Pagination page={evp.page} pageCount={evp.pageCount} total={evp.total} onPrev={evp.prev} onNext={evp.next} label="events" />
        </>
      )}

      <Dialog.Root open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <Dialog.Content maxWidth="480px">
          <Dialog.Title>Edit live event</Dialog.Title>
          <Dialog.Description size="2" mb="3" color="gray">
            Overrides win over the venue default. Leave a field blank to use the venue default.
          </Dialog.Description>
          <Flex direction="column" gap="3">
            <label>
              <Text as="div" size="1" mb="1" color="gray">Display name</Text>
              <TextField.Root
                value={editForm.name}
                placeholder={editTarget?.quiz?.name ?? 'Quiz name'}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </label>
            <label>
              <Text as="div" size="1" mb="1" color="gray">Start time (override)</Text>
              <TextField.Root
                value={editForm.startTimeOverride}
                placeholder="Venue default"
                onChange={(e) => setEditForm((f) => ({ ...f, startTimeOverride: e.target.value }))}
              />
            </label>
            <label>
              <Text as="div" size="1" mb="1" color="gray">Prizes (override)</Text>
              <TextArea
                value={editForm.prizesOverride}
                placeholder="Venue default"
                onChange={(e) => setEditForm((f) => ({ ...f, prizesOverride: e.target.value }))}
              />
            </label>
            <label>
              <Text as="div" size="1" mb="1" color="gray">Rules (override)</Text>
              <TextArea
                value={editForm.rulesTextOverride}
                placeholder="Venue default"
                onChange={(e) => setEditForm((f) => ({ ...f, rulesTextOverride: e.target.value }))}
              />
            </label>
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save'}</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
};

// Inline-editable event-name cell. Renders the override when set,
// otherwise falls back to the underlying quiz name. Click swaps to
// an input; Enter or blur commits via PUT /api/events/:id.
function EventNameCell({
  event,
  onRenamed,
}: {
  event: LiveEvent;
  onRenamed: (next: string | null) => void;
}) {
  const display = event.name?.trim() || event.quiz?.name || '—';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(event.name ?? '');

  useEffect(() => {
    if (!editing) setDraft(event.name ?? '');
  }, [event.name, editing]);

  const commit = () => {
    const trimmed = draft.trim();
    const next = trimmed || null;
    setEditing(false);
    if ((event.name ?? null) === next) return;
    const prev = event.name ?? null;
    onRenamed(next);
    api
      .put(`/api/events/${event.id}`, { name: next })
      .catch(() => {
        onRenamed(prev);
      });
  };

  if (!editing) {
    return (
      <Button
        type="button"
        variant="ghost"
        color="gray"
        highContrast
        onClick={() => setEditing(true)}
        title="Click to rename. Leave blank to fall back to the quiz name."
        style={{ cursor: 'text' }}
      >
        {display}
      </Button>
    );
  }

  return (
    <TextField.Root
      autoFocus
      size="1"
      value={draft}
      placeholder={event.quiz?.name || 'Event name'}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') { setDraft(event.name ?? ''); setEditing(false); }
      }}
      maxLength={120}
    />
  );
}

export default EventList;
